import { useEffect, useState } from 'react'

import { getTypeaheadSuggestions } from 'api/search'
import type { TypeaheadSuggestionsGetOptions } from 'api/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDestinationImage } from 'lib/search/destinationImageMap'
import { getSearchItemDestinationId } from 'lib/search/searchItem'

/**
 * Destinations only, and a short list: the place is matched back by id, so extra suggestions
 * for other places are wasted work rather than better odds of a hit.
 */
const TYPEAHEAD_OPTIONS: TypeaheadSuggestionsGetOptions = {
  searchTypes: ['destination', 'landmark'],
  limit: 10,
}

/**
 * Resolve the image for a destination, preferring the curated map.
 *
 * 1. `destinationImageMap` — hand-picked images for the destinations we merchandise. Only ~160
 *    places are listed, so most searched destinations fall through.
 * 2. Typeahead — the same suggestions endpoint the search bar uses, which carries an image for
 *    far more places. It is keyed by text, so the place name is queried and the result is matched
 *    back by place id; a name that resolves to a different place contributes nothing.
 *
 * Returns `undefined` while the typeahead lookup is in flight, when it fails, and when neither
 * source knows the place — callers must handle an imageless destination.
 */
function useDestinationImageId(placeId?: string, placeName?: string): string | undefined {
  const mappedImageId = placeId ? getDestinationImage(placeId) : undefined
  const [typeaheadImageId, setTypeaheadImageId] = useState<string | undefined>(undefined)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)

  useEffect(() => {
    setTypeaheadImageId(undefined)
    if (mappedImageId || !placeId || !placeName) return

    let cancelled = false
    getTypeaheadSuggestions(placeName, currentRegionCode, TYPEAHEAD_OPTIONS)
      .then((searchItems) => {
        if (cancelled) return
        const match = searchItems.find(item => getSearchItemDestinationId(item) === placeId)
        setTypeaheadImageId(match?.format.imageId)
      })
      // An imageless modal is a fine outcome, so a failed lookup is not worth surfacing.
      .catch(() => undefined)

    return () => { cancelled = true }
  }, [mappedImageId, placeId, placeName, currentRegionCode])

  return mappedImageId || typeaheadImageId
}

export default useDestinationImageId
